import '../Styles/PositionMonitorStack.css';
import useStrategyDashboardStore from '../Stores/strategyDashboardStore';

function parseNumber(value) {
  if (value === null || value === undefined || value === '') return null;

  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function formatMoney(value) {
  if (value === null || value === undefined || value === '') return '—';

  const number = Number(value);
  if (!Number.isFinite(number)) return String(value);

  const sign = number < 0 ? '-' : '';
  const absoluteValue = Math.abs(number);

  return `${sign}$${absoluteValue.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

function formatDate(value) {
  if (!value) return null;

  const text = String(value);

  // Prevent timezone shift for date-only values like 2026-05-26
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) {
    const [year, month, day] = text.slice(0, 10).split('-');
    return `${Number(month)}/${Number(day)}`;
  }

  const date = new Date(value);

  if (!Number.isNaN(date.getTime())) {
    return `${date.getMonth() + 1}/${date.getDate()}`;
  }

  return text;
}

function formatStrike(value) {
  if (value === null || value === undefined || value === '') return null;

  const number = Number(value);
  if (Number.isNaN(number)) return String(value);

  return number.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

function getStructureLabel(position) {
  return (
    position?.position_type ||
    position?.structure_type ||
    position?.trade_title ||
    '—'
  );
}

function getEntryDisplay(position) {
  return (
    formatDate(position?.entry_date) ||
    formatDate(position?.opened_at) ||
    '—'
  );
}

function getExitDisplay(position) {
  return (
    formatDate(position?.close_date) ||
    formatDate(position?.closed_at) ||
    '—'
  );
}

function getOpexDisplay(position) {
  return (
    formatDate(position?.expiration_date) ||
    formatDate(position?.expiration_day) ||
    formatDate(position?.opex_date) ||
    '—'
  );
}

function getPremiumDisplay(position) {
  const credit = parseNumber(position?.entry_credit ?? position?.credit);
  const debit = parseNumber(position?.entry_debit ?? position?.debit);

  if (credit !== null && credit > 0) {
    return formatMoney(credit);
  }

  if (debit !== null && debit > 0) {
    return formatMoney(-debit);
  }

  if (debit !== null && debit < 0) {
    return formatMoney(debit);
  }

  return '—';
}

function TradeLeg({ action, actionType, strike, type }) {
  if (!strike) return null;

  return (
    <div className="tradeLeg">
      <span className={`tradeLegAction ${actionType}`}>
        {action}
      </span>
      <span className="tradeLegStrike">
        {strike}
        {type}
      </span>
    </div>
  );
}

function TradeSetupDisplay({ position }) {
  const shortCall = formatStrike(position?.short_call);
  const longCall = formatStrike(position?.long_call);
  const shortPut = formatStrike(position?.short_put);
  const longPut = formatStrike(position?.long_put);

  const hasCalls = shortCall || longCall;
  const hasPuts = shortPut || longPut;

  if (!hasCalls && !hasPuts) return '—';

  return (
    <div className="tradeSetup">
      {hasCalls && (
        <div className="tradeSide">
          <div className="tradeSideLabel">Calls</div>
          <TradeLeg action="S" actionType="sell" strike={shortCall} type="C" />
          <TradeLeg action="B" actionType="buy" strike={longCall} type="C" />
        </div>
      )}

      {hasPuts && (
        <div className="tradeSide">
          <div className="tradeSideLabel">Puts</div>
          <TradeLeg action="S" actionType="sell" strike={shortPut} type="P" />
          <TradeLeg action="B" actionType="buy" strike={longPut} type="P" />
        </div>
      )}
    </div>
  );
}

function EmptyRow({ colSpan, label }) {
  return (
    <tr>
      <td colSpan={colSpan} className="emptyTableCell">
        {label}
      </td>
    </tr>
  );
}

function PositionsTable({
  title,
  subtitle,
  rows,
  exitMode = false,
}) {
  return (
    <div className="panel monitorCard">
      <div className="panelHeader compactPanelHeader">
        <div>
          <h2>{title}</h2>
          <p>{subtitle}</p>
        </div>
      </div>

      <table className="dataTable compactTable positionMonitorTable">
        <thead>
          <tr>
            <th>Strat</th>
            <th>Type</th>
            <th>{exitMode ? 'Exit' : 'Ent'}</th>
            <th>Setup</th>
            <th>Exp</th>
            <th>Prem</th>
          </tr>
        </thead>

        <tbody>
          {rows?.length > 0 ? (
            rows.map(position => (
              <tr
                key={`${position.strategy_key}-${position.id}-${position.entry_date || position.opened_at}`}
              >
                <td>{position.strategy || position.strategy_key || '—'}</td>
                <td>{getStructureLabel(position)}</td>
                <td>{exitMode ? getExitDisplay(position) : getEntryDisplay(position)}</td>
                <td>
                  <TradeSetupDisplay position={position} />
                </td>
                <td>{getOpexDisplay(position)}</td>
                <td>{getPremiumDisplay(position)}</td>
              </tr>
            ))
          ) : (
            <EmptyRow
              colSpan={6}
              label={exitMode ? 'No positions closed today.' : 'No open positions.'}
            />
          )}
        </tbody>
      </table>
    </div>
  );
}

export default function PositionMonitorStack() {
  const openPositions = useStrategyDashboardStore(
    state => state.tradeTracking.openPositions
  );

  const closedToday = useStrategyDashboardStore(
    state => state.tradeTracking.closedToday
  );

  return (
    <section className="positionMonitorStack">
      <PositionsTable
        title="Open Positions"
        subtitle="Current active trades"
        rows={openPositions}
      />

      <PositionsTable
        title="Closed Today"
        subtitle="Realized exits"
        rows={closedToday}
        exitMode
      />
    </section>
  );
}





// import '../Styles/PositionMonitorStack.css';
// import useStrategyDashboardStore from '../Stores/strategyDashboardStore';
//
// function formatMoney(value) {
//   if (value === null || value === undefined || value === '') return '—';
//
//   const number = Number(value);
//   if (Number.isNaN(number)) return String(value);
//
//   const sign = number > 0 ? '+' : '';
//   return `${sign}$${number.toLocaleString(undefined, {
//     minimumFractionDigits: 0,
//     maximumFractionDigits: 2,
//   })}`;
// }
//
// function formatTime(value) {
//   if (!value) return '—';
//
//   const date = new Date(value);
//
//   if (!Number.isNaN(date.getTime())) {
//     return date.toLocaleTimeString('en-US', {
//       hour: 'numeric',
//       minute: '2-digit',
//     });
//   }
//
//   return String(value);
// }
//
// function getStructureLabel(position) {
//   return (
//     position?.position_type ||
//     position?.structure_type ||
//     position?.trade_title ||
//     '—'
//   );
// }
//
// function getEntryDisplay(position) {
//   return (
//     position?.entry_time ||
//     formatTime(position?.opened_at) ||
//     position?.entry_date ||
//     '—'
//   );
// }
//
// function getExitDisplay(position) {
//   return (
//     position?.close_time ||
//     formatTime(position?.closed_at) ||
//     position?.close_date ||
//     '—'
//   );
// }
//
// function getPnlValue(position) {
//   return (
//     position?.realized_pnl ??
//     position?.pnl ??
//     position?.unrealized_pnl ??
//     null
//   );
// }
//
// function pnlClass(value) {
//   const number = Number(value);
//
//   if (Number.isNaN(number)) return '';
//
//   if (number > 0) return 'positive';
//   if (number < 0) return 'negative';
//
//   return '';
// }
//
// function EmptyRow({ colSpan, label }) {
//   return (
//     <tr>
//       <td colSpan={colSpan} className="emptyTableCell">
//         {label}
//       </td>
//     </tr>
//   );
// }
//
// function PositionsTable({
//   title,
//   subtitle,
//   rows,
//   exitMode = false,
// }) {
//   return (
//     <div className="panel monitorCard">
//       <div className="panelHeader compactPanelHeader">
//         <div>
//           <h2>{title}</h2>
//           <p>{subtitle}</p>
//         </div>
//       </div>
//
//       <table className="dataTable compactTable">
//         <thead>
//           <tr>
//             <th>Strategy</th>
//             <th>Structure</th>
//             <th>{exitMode ? 'Exit' : 'Entry'}</th>
//             <th>P/L</th>
//           </tr>
//         </thead>
//
//         <tbody>
//           {rows?.length > 0 ? (
//             rows.map(position => {
//               const pnl = getPnlValue(position);
//
//               return (
//                 <tr key={`${position.strategy_key}-${position.id}-${position.entry_date}`}>
//                   <td>{position.strategy || position.strategy_key || '—'}</td>
//                   <td>{getStructureLabel(position)}</td>
//                   <td>{exitMode ? getExitDisplay(position) : getEntryDisplay(position)}</td>
//                   <td className={pnlClass(pnl)}>{formatMoney(pnl)}</td>
//                 </tr>
//               );
//             })
//           ) : (
//             <EmptyRow
//               colSpan={4}
//               label={exitMode ? 'No positions closed today.' : 'No open positions.'}
//             />
//           )}
//         </tbody>
//       </table>
//     </div>
//   );
// }
//
// export default function PositionMonitorStack() {
//   const openPositions = useStrategyDashboardStore(
//     state => state.tradeTracking.openPositions
//   );
//
//   const closedToday = useStrategyDashboardStore(
//     state => state.tradeTracking.closedToday
//   );
//
//   return (
//     <section className="positionMonitorStack">
//       <PositionsTable
//         title="Open Positions"
//         subtitle="Current active trades"
//         rows={openPositions}
//       />
//
//       <PositionsTable
//         title="Closed Today"
//         subtitle="Realized exits"
//         rows={closedToday}
//         exitMode
//       />
//     </section>
//   );
// }